import React, { useState, useLayoutEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { Link } from '@mui/material';
import { PersonOutline } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@/shared/ui';
import { useAuth } from '@/shared/context/AuthContext';
import AuthPageShell, { authFieldSx } from '@/modules/identity/components/AuthPageShell';

const ForgotPassword: React.FC = () => {
  const { t } = useTranslation('identity');
  const [success, setSuccess] = useState(false);
  const { forgotPassword, isAuthenticated, loading, error } = useAuth();
  const navigate = useNavigate();

  useLayoutEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  const formSchema = Yup.object({
    username: Yup.string().required(t('validation.usernameRequired')),
  });

  return (
    <AuthPageShell>
      <Box sx={{ width: '100%' }}>
        <Typography component="h1" variant="h3" fontWeight={600} align="center" sx={{ mb: 1.5 }}>
          {t('forgotPassword.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 4 }}>
          {t('forgotPassword.subtitle')}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ width: '100%', mb: 2 }}>
            {t('forgotPassword.success')}
          </Alert>
        )}

        <Formik
          initialValues={{ username: '' }}
          validationSchema={formSchema}
          onSubmit={async (values, { setSubmitting }) => {
            try {
              await forgotPassword(values.username);
              setSuccess(true);
            } catch {
              // Error is displayed via AuthContext `error` state
            } finally {
              setSubmitting(false);
            }
          }}
          validateOnBlur={false}
        >
          {({ errors, touched, isSubmitting }) => (
            <Form autoComplete="on">
              <Stack spacing={2.5}>
                <Field
                  as={TextField}
                  name="username"
                  label={t('login.username')}
                  placeholder={t('login.usernamePlaceholder')}
                  variant="outlined"
                  fullWidth
                  autoComplete="username"
                  autoFocus
                  disabled={loading || success}
                  error={touched.username && Boolean(errors.username)}
                  helperText={touched.username && errors.username}
                  sx={authFieldSx}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonOutline fontSize="small" color="action" />
                        </InputAdornment>
                      ),
                    },
                  }}
                />

                <Button
                  type="submit"
                  intent="primary"
                  fullWidth
                  size="large"
                  disabled={isSubmitting || loading || success}
                  startIcon={
                    isSubmitting || loading ? <CircularProgress size={20} /> : undefined
                  }
                >
                  {isSubmitting || loading
                    ? t('forgotPassword.submitting')
                    : t('forgotPassword.submit')}
                </Button>

                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Link component={RouterLink} to="/login" variant="body2">
                    {t('forgotPassword.backToLogin')}
                  </Link>
                </Box>
              </Stack>
            </Form>
          )}
        </Formik>
      </Box>
    </AuthPageShell>
  );
};

export default ForgotPassword;
